/**
 * The four tag calls (exercise-library R21 to R23), and what each does to the caches it touches.
 *
 * · **`GET /exercise/tags`** — the trainer's tags with how many movements carry each, as
 *   `tagUsageQueryOptions`. The editor's suggestions and limit, and the manage dialog's rows.
 * · **`PUT /exercise/{id}/tags`** replaces one movement's whole set and answers with the movement.
 *   Its tags are PATCHED into every cached copy of the catalogue — one per content language — rather
 *   than the thirty-minute catalogue being thrown away and re-read: a tag is the same in every
 *   language, the answer already carries the server's spelling, and re-reading four hundred
 *   movements to change one chip would redraw the table under the trainer for nothing.
 * · **`PATCH /exercise/tags?label=`** renames or merges, and **`DELETE /exercise/tags?label=`**
 *   removes a tag everywhere. Both touch an unknown number of movements, so both drop the
 *   catalogue's root and let it be read again.
 *
 * Every one of them drops `GET /exercise/tags` too: the manage dialog's counts are the server's.
 *
 * The label travels as a query parameter because a tag may hold a slash (ADR 2026-10-10).
 */

import { queryOptions, type QueryClient } from '@tanstack/react-query';

import { exerciseLibraryQueryOptions, exerciseLibraryRoot } from '~/entities/exercise-library';
import { ApiError } from '~/shared/api/mutator';
import {
  deleteExerciseTags,
  getExerciseTags,
  getGetExerciseTagsQueryKey,
  patchExerciseTags,
  putExerciseIdTags,
} from '~/shared/api/generated/endpoints/exercise/exercise';
import type { TrainWithMeModulesExercisesDtosResponsesExerciseTagResponse } from '~/shared/api/generated/model';
import { CONTENT_LANGUAGES, type ContentLanguage } from '~/shared/i18n/contentLanguage';

import type { TagRefusal, TagUsage } from '../model/exerciseTags';

type ApiTag = TrainWithMeModulesExercisesDtosResponsesExerciseTagResponse;

/**
 * The trainer's tags and their counts. A row with a blank label is no tag and is dropped. Thirty
 * minutes, like the catalogue it counts over: every write here drops it, so nothing local can make
 * it stale, and only another device's edit can.
 */
export function tagUsageQueryOptions() {
  return queryOptions({
    queryKey: getGetExerciseTagsQueryKey(),
    queryFn: ({ signal }) => getExerciseTags(undefined, signal),
    staleTime: 30 * 60 * 1000,
    select: (rows): TagUsage[] =>
      rows.flatMap((row) => {
        const label = (row.label ?? '').trim();
        return label === '' ? [] : [{ label, exerciseCount: row.exerciseCount }];
      }),
  });
}

/** Replace one movement's tags and patch the answer into every cached catalogue. */
export async function saveExerciseTags(
  client: QueryClient,
  exerciseId: number,
  labels: readonly string[],
  lang: ContentLanguage,
): Promise<void> {
  const answer = await putExerciseIdTags(exerciseId, { labels: [...labels] }, { lang });
  patchCachedTags(client, exerciseId, answer.tags ?? []);
  await client.invalidateQueries({ queryKey: getGetExerciseTagsQueryKey() });
}

/**
 * Write one movement's tags into each content language's copy of the catalogue.
 *
 * A language whose copy was never read is left unread: `setQueryData` with an updater that answers
 * `undefined` creates nothing, so a cache entry does not appear for a read nobody made.
 */
export function patchCachedTags(
  client: QueryClient,
  exerciseId: number,
  tags: readonly ApiTag[],
): void {
  for (const lang of CONTENT_LANGUAGES) {
    client.setQueryData(exerciseLibraryQueryOptions(lang).queryKey, (items) =>
      items?.map((item) => (item.id === exerciseId ? { ...item, tags: [...tags] } : item)),
    );
  }
}

/** Rename a tag everywhere; renaming onto an existing name merges the two in the typed spelling. */
export async function renameExerciseTag(
  client: QueryClient,
  from: string,
  to: string,
): Promise<void> {
  await patchExerciseTags({ label: from }, { label: to });
  await invalidateTagged(client);
}

/** Remove a tag from every movement that carries it. */
export async function deleteExerciseTag(client: QueryClient, label: string): Promise<void> {
  await deleteExerciseTags({ label });
  await invalidateTagged(client);
}

function invalidateTagged(client: QueryClient): Promise<unknown> {
  return Promise.all([
    client.invalidateQueries({ queryKey: exerciseLibraryRoot }),
    client.invalidateQueries({ queryKey: getGetExerciseTagsQueryKey() }),
  ]);
}

/**
 * Which refusal a failed tag write was, or `null` for a failure that is not one.
 *
 * The three codes the server names (R22). Anything else — a dropped connection, a 500, a 404 for a
 * movement or tag that is no longer there, a 409 `write_busy` while another write to the same tags
 * is in flight, a 429 past thirty writes a minute — is a failure the editor words generically, as
 * „try again", because none of them says anything about the label.
 */
export function tagRefusalOf(error: unknown): TagRefusal | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  switch (error.code) {
    case 'tag_label_invalid':
      return 'invalid';
    case 'tag_limit_per_exercise':
      return 'perExercise';
    case 'tag_limit_per_trainer':
      return 'perTrainer';
    default:
      return null;
  }
}
